/**
 * @module experience/landing
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
// Core Libraries
import { Component, OnInit, OnDestroy, Input, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
// import {SETTINGS, CHANNELS} from '@environment/settings';
import { AppConfig } from '@root/app.config';

// interfaces
import { ConfigData, AuthorableData } from '@experience/repdash/interfaces/aem-content.interface';
import { CustomerSessionPassDetailsLineDetailsPassDetails } from '@support/api/proxies/responses/api-2010.interface';
import { CustomerUsageSummaryResultsLines } from '@support/api/proxies/responses/api-2009.interface';
import { StaticModal } from '@support/shared/widgets/interfaces/static-modal.interface';
import { CustomerLineBillingDetailsAccountLines } from '@support/api/proxies/responses/api-2008.interface';
import { TmoButton } from '@support/shared/widgets/interfaces/button.interface';

// Constants
import {
  PLAN_SERVICES_CONSTANTS,
  ROUTER_CONSTANTS,
} from '@experience/landing/common/constants/line-details.constants';
import { CPS_ROUTES } from '@support/shared/constants/app-routes.constants';

// Service
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { LineLandingService } from '@experience/landing/common/services/line-landing.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { MixedCartService } from '@experience/shared-experience/services/mixed-cart.service';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { BILLING_DETAILS_CONSTANTS } from '@experience/billing-details/billing-details.constants';
import { LineSummary } from '@support/api/proxies/responses/api-2006.interface';
import { CPS_CONSTANTS } from '@experience/change-plan-services/constants/change-plan-services.constants';
import { KeyValueMap } from '@support/shared/interfaces/key-value-map.interface';
import { PERMISSION_CONSTANTS } from '@support/shared/constants/permission.constants';
import { UIPermissionService } from '@support/shared/widgets/services/ui-permission.service';
@Component({
  selector: 'tmo-plan-and-services',
  templateUrl: './plan-and-services.component.html',
})
export class PlanAndServicesComponent implements OnInit, OnDestroy {
  /**
   * Stores the values from AEM content data
   */
  @Input() public contentData: ConfigData;
  /**
   * Stores the values from AEM content data
   */
  @Input() public authorableData: AuthorableData;

  /**
   *  holds true if plan is of type tourist
   */
  @Input() public isTouristPlan: boolean;

  /**
   *  holds true if line status is active
   */
  @Input() public lineStatus: string;

  /**
   *  holds true if line status is active
   */
  @Input() public isActive: boolean;

  /**
   * To store Line Array details
   */
  public lines: CustomerLineBillingDetailsAccountLines[];
  /**
   * Boolean Flag to Open Edit plans and services modal
   */
  public displayModal: boolean;
  /**
   * Flag for Assisted channel i.e. Retail & Care
   */
  // public isAssistedFlow: boolean;

  /**
   * Static modal for cancellation status pop up in retail and care
   */
  public modal: StaticModal;
  /**
   * Event Emitter to trigger events on click of anchor
   */
  public triggerEvent: EventEmitter<Object>;
  /**
   * To store lineDetails Label
   */
  public lineDetails: string;

  /**
   * Button to navigate to managePendingOrders page
   */
  public pendingOrdersBtn: TmoButton;
  /**
   * stores the line usage summary of customer
   */
  @Input() public lineUsageSummary: CustomerUsageSummaryResultsLines;
  /**
   * To store showOnLineDetails Boolean Flag
   */
  @Input() public showOnLineDetails: Boolean;
  /**
   * To store displayPendingOrders Boolean Flag
   */
  @Input() public isPendingOrder: Boolean;
  /**
   * To store displayPendingOrders modal Flag
   */
  @Input() public pendingOrdersModal: Boolean;

  public passDetails: CustomerSessionPassDetailsLineDetailsPassDetails;
  /*
   * To store billing type
   */
  public billingType: Boolean;
  /**
   * Stores cancelled constants value
   */
  public cancelled: string;
  /**
   * Stores active constants value
   */
  public active: string;
  /**
   * To store Plan Service constants
   */
  public PLAN_SERVICE_CONSTANTS: {};
  /**
   * stores constant value of Permission Constant
   */
  public permissionConstants: KeyValueMap;
  /**
   * stores if permission is enabled or disabled
   */
  public permissionEnabled: boolean;
  /**
   * Stores router constants value
   */
  public routerConstants: ConfigData;
  /**
   * mixed cart modal
   */
  public mixedCartModal: StaticModal;
  /**
   * variable to hold the spinner delay time
   */
  public showSpinner: boolean;

  public error: string;

  public broadbandFactsUrl: string;

  @Input() public set broadbandFactsUrlChange(value: string) {
    this.broadbandFactsUrl = value;
  }

  constructor(
    private stateStoreUtilsService: StateStoreUtilsService,
    private commonUtilService: CommonUtilService,
    private objectService: ObjectService,
    private router: Router,
    private lineLandingService: LineLandingService,
    private mixedCartService: MixedCartService,
    private cartSupportService: CartSupportService,
    private appConfig: AppConfig,
    public routeSnapshot: ActivatedRoute,
    private permissionService: UIPermissionService
  ) {
    (this.modal = {
      headerText: '',
      bodyText: '',
      disableDismiss: false,
      disableEsc: true,
      modalName: 'plan-and-services-modal',
      dismissableMask: true,
      buttons: [],
      toggleModalEmitter: new EventEmitter(),
      closableText: '',
    }),
      (this.pendingOrdersBtn = {
        id: PLAN_SERVICES_CONSTANTS.MANAGE_PENDING_ORDERS,
        name: PLAN_SERVICES_CONSTANTS.MANAGE_PENDING_ORDERS,
        class: PLAN_SERVICES_CONSTANTS.BUTTON_CLASS_SECONDARY,
        type: PLAN_SERVICES_CONSTANTS.BUTTON_CLASS_SECONDARY,
      });
    this.PLAN_SERVICE_CONSTANTS = PLAN_SERVICES_CONSTANTS;
    this.cancelled = PLAN_SERVICES_CONSTANTS.CANCELLED;
    this.active = PLAN_SERVICES_CONSTANTS.ACTIVE;
    this.error = PLAN_SERVICES_CONSTANTS.ERROR;
    this.mixedCartModal = {
      headerText: '',
      bodyText: '',
      disableDismiss: true,
      disableEsc: true,
      buttons: [
        {
          id: '',
          type: 'submit',
          name: '',
          class: 'btn btn-primary',
        },
        {
          id: 'ViewCart',
          type: 'cancel',
          name: '',
          class: 'btn btn-secondary',
        },
      ],
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: false,
    };
  }

  /**
   * Loads the initial data of Plans and services Page
   * @returns void
   */
  public ngOnInit(): void {
    this.permissionConstants = PERMISSION_CONSTANTS;
    this.routerConstants = ROUTER_CONSTANTS;
    // this.isAssistedFlow = (SETTINGS.CHANNEL.toLowerCase() !== CHANNELS.WEB);
    this.modal = this.lineLandingService.createCancellationModal(this.contentData);
    this.displayModal = false;
    this.lineDetails = PLAN_SERVICES_CONSTANTS.LINEDETAILS;
    const getBillingType = this.stateStoreUtilsService.getBillingTypeOfCustomerInFocus();
    if (this.commonUtilService.isNotEmpty(getBillingType)) {
      this.billingType = getBillingType.toLocaleUpperCase() === BILLING_DETAILS_CONSTANTS.PREPAID;
    }
    this.mixedCartModal.headerText = this.contentData.clearYourCart;
    this.mixedCartModal.bodyText = this.contentData.clearOrViewCart;
    this.mixedCartModal.buttons[0].name = this.contentData.continue;
    this.mixedCartModal.buttons[0].id = this.contentData.continue;
    this.mixedCartModal.buttons[1].name = this.contentData.viewCart;
    this.permissionEnabled = this.permissionService.checkMultiplePermissions(
      ROUTER_CONSTANTS.LINE_LANDING,
      PERMISSION_CONSTANTS.CHANGE_RTPLN_LOST_STOLEN,
      PERMISSION_CONSTANTS.CHANGE_RATE_PLAN,
      'RepOnly',
      'common'
    );
  }

  /**
   * Unsubscribes for all subscriptions
   * @returns void
   */
  public ngOnDestroy(): void {}

  /**
   * Sets the url of the page on click of demand pass button
   * @param url : stores the value of the url
   */
  public setPreviousUrl(url: string): void {
    this.stateStoreUtilsService.setPreviousUrl(url);
    this.stateStoreUtilsService.setCPSEntryPoint(url);
  }

  /**
   * called function to activate and deactivate modal
   * @param openModal: holds a flag to open the modal
   * @return void
   */
  public openModal(event: EventEmitter<Object>): void {
    this.triggerEvent = event;
    this.modal.toggleModalEmitter.emit({ open: true });
  }

  /**
   * Method to close Cancelled msisdn Modal.
   * @returns void
   */
  public closeModal(): void {
    this.displayModal = false;
    this.modal.toggleModalEmitter.emit({ close: true });
    const target = 'target';
    this.triggerEvent[target].focus();
  }

  /**
   * Method to open modal if msisdn is cancelled or route to respective page if msisdn is active
   * @param event : event to open modal
   * @returns void
   */
  public routeEvent(event: EventEmitter<Object>): void {
    let selectedLines = [];
    const cart = this.stateStoreUtilsService.getCart();
    const account = this.stateStoreUtilsService.getBillingBlockDetails();
    if (cart && cart.cartId) {
      if (!this.mixedCartService.showLineSelector) {
        this.mixedCartModal.toggleModalEmitter.emit({ open: true, close: false });
        return;
      }
    }
    if (this.objectService.get(account, 'lines')) {
      selectedLines = account.lines.filter((selectedLine: LineSummary) => {
        return selectedLine.lineOfServiceId === this.stateStoreUtilsService.getLineId();
      });
    }
    if (selectedLines.length && selectedLines[0].lineStatus === PLAN_SERVICES_CONSTANTS.CANCELLED) {
      this.openModal(event);
    } else {
      this.router.navigate([CPS_ROUTES.SESSION_PASS]);
    }
  }

  /**
   * Method is handling click functionality on mouse click
   * @param event : Mouse click
   * @returns void
   */
  public handleClickEvent(event: Event, isNewTab: boolean): void {
    // if (this.isAssistedFlow) {
    //   if (event.srcElement.id.toLowerCase() === PLAN_SERVICES_CONSTANTS.BACK) {
    //     this.closeModal();
    //   }
    //   else {
    //     this.router.navigate([PLAN_SERVICES_CONSTANTS.RESUMECANCELLATION]);
    //   }
    // }
    // else {
    this.commonUtilService.routeToGivenUrl(this.appConfig.getConfig('contactUsURL'), isNewTab);
    // }
  }

  /**
   * Method is handling click functionality on mouse click
   * @param event : Mouse click
   * @returns void
   */
  public handleCartModalClickEvent(event: Event): void {
    this.mixedCartModal.toggleModalEmitter.emit({
      open: false,
      close: true,
    });
    if (event[CPS_CONSTANTS.ID] === PLAN_SERVICES_CONSTANTS.CONTINUE) {
      this.showSpinner = true;
      this.cartSupportService.deleteCart().subscribe(
        () => {
          this.cartSupportService.updateCart.emit(true);
          this.stateStoreUtilsService.deleteCart();
          this.showSpinner = false;
          this.router.navigate([CPS_ROUTES.SESSION_PASS]);
        },
        () => {}
      );
    } else {
      this.mixedCartService.showLineSelector = null;
      this.router.navigate([CPS_CONSTANTS.CART_URL]);
    }
  }

  /* *
   * To Open Edit Plan and Service Modal
   */
  public openEditPlanService(): void {
    this.setPreviousUrl(ROUTER_CONSTANTS.LINE_LANDING);
    this.router.navigate([CPS_ROUTES.CHANGE_PLAN]);
  }

  /* *
   * To close line selector modal
   */
  public closeLineSelectorModal(event: boolean): void {
    this.displayModal = event;
  }
}
